import React from 'react'
import { FormikProvider, useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import { Provider } from '../../models/Provider'
import { userCanCreateGlobalProvider } from '../../state/calculations/permissions/userCan'
import useCreateProvider from '../../state/mutations/useCreateProvider'
import { useSession } from '../../state/session'
import setFormErrors from '../../utilities/setFormErrors'
import useFocusInputWithError from '../../utilities/useFocusInputWithError'
import { CheckboxFormik } from '../basics/Checkbox/Checkbox'
import FormButtons from '../basics/FormButtons/FormButtons'
import { TextInputFormik } from '../basics/TextInput/TextInput'

import styles from './EditProviderForm.module.scss'

// TODO: Does only allow creating providers for now, editing TBD

type EditProviderFormProps = {
  onClose?(provider?: Provider): void
}

export default React.memo(function EditProviderForm({ onClose }: EditProviderFormProps) {
  const { t } = useTranslation()

  const { user: currentUser } = useSession()

  const createProvider = useCreateProvider()
  const formik = useFormik({
    initialValues: { name: '', global: false },
    enableReinitialize: true,
    onSubmit: async (values, { setStatus, setErrors }) => {
      try {
        setStatus()

        const provider = await createProvider.mutateAsync(values)

        onClose?.(provider)
      } catch (err) {
        setFormErrors({ setStatus, setErrors, t }, err)
      }
    },
  })

  useFocusInputWithError(formik)

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={styles.EditProviderForm}>
        <h1>{t('provider.create new provider')}</h1>

        <TextInputFormik name="name" label={t('provider.field name')} required />

        {userCanCreateGlobalProvider(currentUser) && (
          <CheckboxFormik name="global" label={t('provider.field global')} />
        )}

        <FormButtons {...formik} onClose={() => onClose?.()} />
      </form>
    </FormikProvider>
  )
})
